import { AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { X, ChevronUp, Wallet, Clock, Boxes } from 'lucide-react';
import { ModalRoot } from '@/components/ui/ModalRoot';
import { Z_MODALS } from '@/lib/zIndex';
import {
  STATION_SPECS,
  getCurrentTier,
  getNextTier,
  getMaxTierLevel,
  isVehicleStationKind,
  type HospitalTier,
  type VehicleStationTier,
} from '@/data/stations';
import { formatEuro } from '@/lib/format';
import { formatBuildTime } from '@/data/buildTime';
import { useGameStore } from '@/state/gameStore';
import type { Station } from '@/state/types';

interface StationUpgradeDialogProps {
  station: Station | null;
  onClose: () => void;
}

/**
 * Side-by-side current → next tier diff. Confirm spends credits and starts
 * construction (or instant in sandbox / threshold).
 */
export function StationUpgradeDialog({ station, onClose }: StationUpgradeDialogProps) {
  const credits = useGameStore((s) => s.credits);
  const mode = useGameStore((s) => s.mode);
  const upgradeStation = useGameStore((s) => s.upgradeStation);
  const pushToast = useGameStore((s) => s.pushToast);

  useEffect(() => {
    if (!station) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [station, onClose]);

  if (!station) {
    // Phase 7.1.2 — keep AnimatePresence empty so framer-motion can settle.
    return null;
  }

  const spec = STATION_SPECS[station.kind];
  const current = getCurrentTier(station.kind, station.level);
  const next = getNextTier(station.kind, station.level);
  const atMax = next === null;
  const cost = next?.cost ?? 0;
  const affordable = mode === 'sandbox' || credits >= cost;
  const maxLevel = getMaxTierLevel(station.kind);

  const handleConfirm = () => {
    if (!next) return;
    const ok = upgradeStation(station.id);
    if (!ok) {
      pushToast({ kind: 'error', text: 'Ausbau fehlgeschlagen.' });
      return;
    }
    const isInstant = mode === 'sandbox' || next.buildTimeHours === 0;
    pushToast({
      kind: 'success',
      text: isInstant
        ? `${spec.label} „${station.name}" auf Stufe ${station.level + 1} ausgebaut.`
        : `${spec.label} „${station.name}" wird auf Stufe ${station.level + 1} ausgebaut (${formatBuildTime(next.buildTimeHours, mode)}).`,
    });
    onClose();
  };

  return (
    <AnimatePresence>
      <ModalRoot
        onClose={onClose}
        zIndex={Z_MODALS}
        contentClass="w-full max-w-xl rounded-lg border border-border-strong bg-bg-secondary p-6"
      >
          <div className="mb-4 flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-accent-dispatch">Ausbauen</p>
              <h3 id="upgrade-dialog-title" className="font-display text-xl font-semibold">
                {station.name}
              </h3>
              <p className="mt-1 text-xs text-fg-muted">
                {spec.label} · Stufe {station.level} / {maxLevel}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Schließen"
              className="text-fg-muted transition hover:text-fg-primary"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          {atMax ? (
            <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-6 text-center">
              <ChevronUp className="mx-auto mb-2 h-6 w-6 text-fg-muted" aria-hidden />
              <p className="font-semibold text-fg-secondary">Höchste Stufe erreicht</p>
              <p className="mt-1 text-xs text-fg-muted">
                Diese Wache ist vollständig ausgebaut.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <TierColumn
                  title={`Aktuell · Stufe ${station.level}`}
                  tone="muted"
                  kind={station.kind}
                  tier={current}
                />
                <TierColumn
                  title={`Neu · Stufe ${station.level + 1}`}
                  tone="accent"
                  kind={station.kind}
                  tier={next}
                />
              </div>

              <div className="mt-4 rounded-md border border-border-subtle bg-bg-elevated/40 p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-fg-muted">Kosten</span>
                  <span className="font-display text-lg font-bold tabular-nums text-accent-emergency">
                    {formatEuro(cost)}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between text-xs">
                  <span className="text-fg-muted">Bauzeit</span>
                  <span className="tabular-nums">
                    {formatBuildTime(next.buildTimeHours, mode)}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between text-xs">
                  <span className="text-fg-muted">Guthaben danach</span>
                  <span className="tabular-nums">
                    {mode === 'sandbox' ? '∞' : formatEuro(credits - cost)}
                  </span>
                </div>
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-border-strong bg-bg-elevated px-4 py-2 text-sm font-semibold text-fg-primary transition hover:bg-bg-elevated/70"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!affordable}
                  className={[
                    'rounded-md px-4 py-2 text-sm font-semibold text-white transition',
                    affordable
                      ? 'bg-accent-dispatch hover:bg-accent-dispatch-hover'
                      : 'cursor-not-allowed bg-bg-elevated text-fg-muted',
                  ].join(' ')}
                >
                  {affordable ? `Ausbauen (${formatEuro(cost)})` : 'Nicht genug Credits'}
                </button>
              </div>
            </>
          )}
      </ModalRoot>
    </AnimatePresence>
  );
}

interface TierColumnProps {
  title: string;
  tone: 'muted' | 'accent';
  kind: Station['kind'];
  tier: VehicleStationTier | HospitalTier;
}

function TierColumn({ title, tone, kind, tier }: TierColumnProps) {
  const isVehicle = isVehicleStationKind(kind);
  const slotLabel = isVehicle ? 'Slots' : 'Patienten';
  const slotValue = isVehicle
    ? (tier as VehicleStationTier).vehicleSlots
    : (tier as HospitalTier).patientCapacity;

  return (
    <div
      className={[
        'rounded-md border p-4',
        tone === 'accent'
          ? 'border-accent-dispatch/40 bg-accent-dispatch/10'
          : 'border-border-subtle bg-bg-elevated/30',
      ].join(' ')}
    >
      <p className="mb-3 text-[10px] uppercase tracking-wider text-fg-muted">{title}</p>
      <Row icon={Boxes} label={slotLabel} value={String(slotValue)} />
      <Row icon={Wallet} label="Tägl." value={`-${formatEuro(tier.upkeepPerDay)}`} />
      {tone === 'accent' && (
        <Row icon={Clock} label="Bauzeit" value={`${tier.buildTimeHours} h`} />
      )}
    </div>
  );
}

function Row({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Wallet;
  label: string;
  value: string;
}) {
  return (
    <div className="mt-1.5 flex items-center justify-between text-xs first:mt-0">
      <span className="flex items-center gap-1.5 text-fg-muted">
        <Icon className="h-3 w-3" aria-hidden />
        {label}
      </span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}
